/**
 * 관리자 매뉴얼 PDF — 파일 서버 `REACT_APP_FILE_URL` 아래 `Manuals/Web/` 에 올린 PDF
 *
 * - 우선 `REACT_APP_ADMIN_MANUAL_BASE_URL` (매뉴얼 폴더 URL 직접 지정)
 * - 미설정 시 `REACT_APP_FILE_URL` + `Manuals/Web/`
 * - 파일명: 라우트별 매핑 + 그 외 `{첫경로세그먼트}.pdf`
 */

const WEB_MANUAL_SIGNUP_LOGIN = '[웹 사용 매뉴얼] 회원가입 및 로그인.pdf';
const WEB_MANUAL_BASIC_INFO = 'base.pdf';

/** 탭이 있는 페이지의 현재 탭 인덱스를 보관하는 레지스트리 */
const _pageTabRegistry = {};

/**
 * 특정 페이지의 현재 탭 인덱스를 저장한다.
 * @param {string} pageKey - 라우트 첫 세그먼트 (예: 'SafetyWorkManagement')
 * @param {number} tab - 현재 탭 인덱스
 */
export function setPageTab(pageKey, tab) {
  _pageTabRegistry[pageKey] = tab;
}

/**
 * 특정 페이지의 현재 탭 인덱스를 반환한다.
 * @param {string} pageKey
 * @returns {number} 저장된 탭 인덱스 (없으면 0)
 */
export function getPageTab(pageKey) {
  return _pageTabRegistry[pageKey] ?? 0;
}

/** SafetyWorkManagement 탭별 PDF 파일명 */
const SAM_TAB_PDF = {
  0: 'SafetyWorkManagement_tab0.pdf',
  1: 'SafetyWorkManagement_tab1.pdf',
};

/** 기준정보관리 하위 5메뉴 → 동일 PDF */
const BASIC_INFO_SEGMENTS = new Set([
  'Company',
  'Plant',
  'Member',
  'Department',
  'Gongjong',
]);

/** `REACT_APP_FILE_URL` 기준 Web 매뉴얼 상대 경로 (슬래시 없음) */
const ADMIN_MANUAL_WEB_REL_PATH = 'files/Manuals/Web';

function ensureTrailingSlash(url) {
  return url.endsWith('/') ? url : `${url}/`;
}

/**
 * `REACT_APP_FILE_URL` + `Manuals/Web/`
 * @returns {string} 끝에 `/` 포함. `REACT_APP_FILE_URL` 미설정 시 빈 문자열
 */
export function getDefaultAdminManualBaseUrl() {
  const fileUrl = (process.env.REACT_APP_FILE_URL || '').trim();
  if (!fileUrl) return '';
  return `${ensureTrailingSlash(fileUrl)}${ADMIN_MANUAL_WEB_REL_PATH}/`;
}

/**
 * @param {string} pathname `location.pathname`
 * @returns {string} 첫 세그먼트, 루트 `/` 로그인은 빈 문자열
 */
export function getAdminManualPageKey(pathname) {
  if (!pathname || typeof pathname !== 'string') return '';
  const seg = pathname.replace(/^\/+/u, '').split('/')[0];
  return seg || '';
}

/**
 * 서버에 존재하는 PDF 파일명 (경로 한 개당 하나)
 * @param {string} pathname `location.pathname`
 * @returns {string}
 */
export function getAdminManualPdfFileName(pathname) {
  const seg = getAdminManualPageKey(pathname);
  if (!seg) {
    return WEB_MANUAL_SIGNUP_LOGIN;
  }
  if (BASIC_INFO_SEGMENTS.has(seg)) {
    return WEB_MANUAL_BASIC_INFO;
  }
  if (seg === 'Aicall') {
    return 'Alcall.pdf';
  }
  if (seg === 'SafetyWorkManagement') {
    const tab = getPageTab('SafetyWorkManagement');
    return SAM_TAB_PDF[tab] ?? 'SafetyWorkManagement.pdf';
  }
  return `${seg}.pdf`;
}

/**
 * @param {string} baseRaw `process.env.REACT_APP_ADMIN_MANUAL_BASE_URL`
 * @returns {string} 절대 URL, 끝에 `/` 포함. 미설정 시 빈 문자열
 */
export function resolveAdminManualBaseAbs(baseRaw) {
  const raw = (baseRaw || '').trim();
  if (!raw) return '';
  if (/^https?:\/\//iu.test(raw)) {
    return ensureTrailingSlash(raw);
  }
  if (typeof window === 'undefined' || !window.location) {
    return ensureTrailingSlash(raw);
  }
  if (raw.startsWith('//')) {
    return ensureTrailingSlash(`${window.location.protocol}${raw}`);
  }
  const path = raw.startsWith('/') ? raw : `/${raw}`;
  return ensureTrailingSlash(`${window.location.origin}${path}`);
}

/**
 * @param {string} baseRaw 베이스 URL (빈 값이면 `getDefaultAdminManualBaseUrl()`)
 * @param {string} pathname `location.pathname`
 * @returns {string} 절대 PDF URL
 */
export function buildAdminManualPdfUrl(baseRaw, pathname) {
  const rawBase = (baseRaw || '').trim() || getDefaultAdminManualBaseUrl();
  const base = resolveAdminManualBaseAbs(rawBase);
  if (!base) return '';
  const fileName = getAdminManualPdfFileName(pathname);
  return `${base}${encodeURIComponent(fileName)}`;
}

/**
 * PDF 존재 여부 확인 (HEAD 후 필요 시 Range GET).
 *
 * @param {string} url
 * @returns {Promise<{ ok: true } | { ok: false, reason: 'not_found' | 'network' }>}
 */
export async function probeAdminManualPdf(url) {
  try {
    let res = await fetch(url, { method: 'HEAD', mode: 'cors', cache: 'no-store' });
    if (res.ok) return { ok: true };
    if (res.status === 405 || res.status === 501) {
      res = await fetch(url, {
        method: 'GET',
        headers: { Range: 'bytes=0-0' },
        mode: 'cors',
        cache: 'no-store',
      });
      if (res.ok || res.status === 206) return { ok: true };
    }
    if (res.status >= 400) return { ok: false, reason: 'not_found' };
    return { ok: false, reason: 'not_found' };
  } catch {
    return { ok: false, reason: 'network' };
  }
}
